import { useMutation } from "@tanstack/react-query";
import { useState, type ChangeEvent } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, Modal, Section, useToast, type Requirement } from "@/components/ui";
import { CustomNsNotice } from "@/components/domains/parts";
import { downloadBlob } from "@/lib/download";
import { domainsApi, type Domain, type Zone, type ZoneImportPreview } from "@/lib/domains";

/**
 * BIND zone import/export (PHP-3942, AC10-11). Export is a plain authenticated download; import previews
 * the file (additions, replacements, rejected lines) before anything changes, through the same
 * preview-then-apply write path as every other DNS change here (AC12).
 */
export function ZoneFileSection({
  domain,
  managed,
  editable,
  editRequires,
  onApplied,
}: {
  domain: Domain;
  managed: boolean;
  editable: boolean;
  editRequires: Requirement;
  onApplied: (next: Zone) => void | Promise<void>;
}) {
  const { orgId } = useOrganization();
  const [importOpen, setImportOpen] = useState(false);

  const exportMutation = useMutation({
    mutationFn: () => domainsApi.exportZone(orgId, domain.id),
    onSuccess: (blob) => downloadBlob(blob, `${domain.fqdn}.zone`),
  });

  if (!managed) {
    return (
      <Section title="Zone file" subtitle="Download or import this domain's records as a BIND zone file">
        <CustomNsNotice />
      </Section>
    );
  }

  return (
    <Section
      title="Zone file"
      subtitle="Download or import this domain's records as a BIND zone file"
      action={
        <div className="flex gap-2">
          <Button
            size="lg"
            variant="secondary"
            loading={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
          >
            Export
          </Button>
          {editable && (
            <Button size="lg" variant="secondary" requires={editRequires} onClick={() => setImportOpen(true)}>
              Import
            </Button>
          )}
        </div>
      }
    >
      <p className="text-sm text-fg-subtle">
        Export a BIND-format copy of every record, for backup or to move to another provider, or import one to
        add or replace records here.
      </p>
      {exportMutation.isError && <ErrorAlert error={exportMutation.error} />}
      {importOpen && (
        <ZoneImportModal
          domain={domain}
          onClose={() => setImportOpen(false)}
          onApplied={async (next) => {
            await onApplied(next);
            setImportOpen(false);
          }}
        />
      )}
    </Section>
  );
}

function ZoneImportModal({
  domain,
  onClose,
  onApplied,
}: {
  domain: Domain;
  onClose: () => void;
  onApplied: (next: Zone) => void | Promise<void>;
}) {
  const { orgId } = useOrganization();
  const toast = useToast();
  const [text, setText] = useState("");
  const [previewed, setPreviewed] = useState<{ text: string; result: ZoneImportPreview } | null>(null);

  const setTextAndClearPreview = (value: string) => {
    setText(value);
    setPreviewed(null);
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void file.text().then(setTextAndClearPreview);
  };

  const preview = useMutation({
    mutationFn: () => domainsApi.previewZoneImport(orgId, domain.id, text),
    onSuccess: (result) => setPreviewed({ text, result }),
  });
  const apply = useMutation({
    mutationFn: () => domainsApi.applyZoneImport(orgId, domain.id, text),
    onSuccess: async (next) => {
      await onApplied(next);
      toast.show({ tone: "success", title: "Zone file imported" });
    },
  });

  // A stale preview (the text changed since) can't be confirmed: it must be re-previewed first.
  const stale = previewed?.text !== text;
  const canConfirm = Boolean(previewed) && !stale && previewed!.result.has_changes;

  return (
    <Modal
      open
      onClose={() => {
        if (preview.isPending || apply.isPending) return;
        onClose();
      }}
      width={640}
      title="Import a zone file"
      subtitle="Upload a BIND-format file, or paste its contents below"
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          {previewed && !stale ? (
            <Button
              variant="brand"
              size="lg"
              loading={apply.isPending}
              disabledReason={!canConfirm ? "Nothing to import" : undefined}
              onClick={() => apply.mutate()}
            >
              Confirm import
            </Button>
          ) : (
            <Button
              variant="brand"
              size="lg"
              loading={preview.isPending}
              disabledReason={!text.trim() ? "Add a zone file to preview" : undefined}
              onClick={() => preview.mutate()}
            >
              Preview
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-border py-3 text-sm text-fg-subtle hover:bg-muted">
          Upload a .zone or .txt file
          <input type="file" accept=".txt,.zone,.bind,text/plain" className="sr-only" onChange={onFile} />
        </label>
        <textarea
          className="min-h-[160px] w-full rounded-lg border border-border bg-surface p-3 font-mono text-xs text-fg"
          placeholder={`$ORIGIN ${domain.fqdn}.\n@\t3600\tIN\tA\t76.223.100.4`}
          value={text}
          spellCheck={false}
          onChange={(e) => setTextAndClearPreview(e.target.value)}
        />
      </div>
      {preview.isError && <ErrorAlert error={preview.error} />}
      {previewed && !stale && <ZoneImportResults result={previewed.result} />}
      <ErrorAlert error={apply.error} />
    </Modal>
  );
}

function ZoneImportResults({ result }: { result: ZoneImportPreview }) {
  if (!result.has_changes && result.rejected.length === 0) {
    return <p className="text-sm text-fg-subtle">No changes — every record in the file already matches.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {!result.has_changes && (
        <p className="text-sm text-fg-subtle">Nothing to import, but some lines were rejected below.</p>
      )}
      {result.additions.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-xl bg-[#eef8f3] p-3.5">
          <p className="text-[13px] font-semibold text-[#007955]">Additions ({result.additions.length})</p>
          {result.additions.map((r, i) => (
            <p key={i} className="font-mono text-xs break-all text-[#5c5551]">
              {r.type} {r.name} → {r.content}
            </p>
          ))}
        </div>
      )}
      {result.replacements.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-xl bg-[#fef4e5] p-3.5">
          <p className="text-[13px] font-semibold text-[#995c05]">
            Replacements ({result.replacements.length})
          </p>
          {result.replacements.map((r, i) => (
            <p key={i} className="font-mono text-xs break-all text-[#5c5551]">
              {r.type} {r.name} → {r.content}
            </p>
          ))}
        </div>
      )}
      {result.rejected.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-xl border border-[#ffc4bb] bg-[#fff1ee] p-3.5">
          <p className="text-[13px] font-semibold text-[#d6342f]">
            Rejected lines ({result.rejected.length})
          </p>
          {result.rejected.map((r, i) => (
            <p key={i} className="text-xs text-[#5c5551]">
              <span className="font-mono break-all">{r.line}</span> — {r.reason}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
